import {
  CSSProperties,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { RoundStory } from './RoundStory';
import { SquareStory } from './SquareStory';
import { StoryAnimation, StoryDisplay, StoryWithShoppables } from '../enums';
import { AdditionalProductFragment } from '../graphql/generated/graphql';
import { useGetStory } from '../hooks/story/useGetStory';
import { useVideo } from '../hooks/story/useVideo';
import { useDialogContext } from '../providers/DialogProvider';
import { OpenDialogPayload, StoryBorder } from '../types';
import { storyAnimationsImplyVideo } from '../utils';

export interface StoryDisplayOptions {
  animations: StoryAnimation[];
  border: StoryBorder;
  borderColor: NonNullable<CSSProperties['borderColor']>;
  display: StoryDisplay;
  font: NonNullable<CSSProperties['font']>;
  withPlayIcon: boolean;
  withRadius: boolean;
  withShoppables: StoryWithShoppables;
  withTitle: boolean;
}

export interface StoryStyle extends CSSProperties {
  _playIcon?: CSSProperties;
}

interface Props {
  id: string;
  company: string;
  product?: string;
  displayOptions: StoryDisplayOptions;
  style?: StoryStyle;
  isActive?: boolean;
  preload?: HTMLVideoElement['preload'];
  extraDialogPayload?: Partial<OpenDialogPayload>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export const Story = memo(
  ({
    id,
    company,
    product,
    isActive = true,
    preload,
    displayOptions,
    style,
    extraDialogPayload,
    onMouseEnter,
    onMouseLeave,
  }: Props) => {
    const { openDialog, prefetchDialog } = useDialogContext();
    const _withVideo = storyAnimationsImplyVideo(displayOptions.animations);

    const withShoppables = useMemo(() => {
      if (displayOptions.display == StoryDisplay.Round) {
        return false;
      }
      return (
        displayOptions.withShoppables === StoryWithShoppables.On ||
        (displayOptions.withShoppables === StoryWithShoppables.OnActive &&
          isActive)
      );
    }, [displayOptions.display, displayOptions.withShoppables, isActive]);

    const { story } = useGetStory({
      id,
      company,
      withStoryPage: _withVideo || withShoppables,
    });

    const { video, videoRef, restartVideo, stopVideo } = useVideo({
      story,
    });

    const [isPlaying, setIsPlaying] = useState(
      isActive && displayOptions.animations.includes(StoryAnimation.Autoplay)
    );
    const isPulsating = useMemo(
      () =>
        isActive && displayOptions.animations.includes(StoryAnimation.Pulse),
      [displayOptions.animations, isActive]
    );

    const payload: OpenDialogPayload | undefined = useMemo(
      () =>
        story && {
          company,
          product:
            product ??
            story.Products?.[0]?.Slug ??
            (() => {
              throw new Error('Product not found');
            })(),
          locale: story.locale,
          story: story.Slug,
          ...extraDialogPayload,
        },
      [company, extraDialogPayload, product, story]
    );

    const handleOpenDialog = useCallback(
      (ap?: AdditionalProductFragment) => {
        if (!payload) return;
        openDialog({
          ...payload,
          ...(ap && { additionalProduct: ap.id }),
        });
      },
      [payload, openDialog]
    );

    const handlePreloadDialog = useCallback(() => {
      if (!payload) return;
      prefetchDialog(payload);
    }, [payload, prefetchDialog]);

    const handleMouseEnter = useCallback(() => {
      handlePreloadDialog();

      if (displayOptions.animations.includes(StoryAnimation.OnHover)) {
        setIsPlaying(true);
      }

      onMouseEnter?.();
    }, [displayOptions.animations, handlePreloadDialog, onMouseEnter]);

    const handleMouseLeave = useCallback(() => {
      if (!displayOptions.animations.includes(StoryAnimation.Autoplay)) {
        setIsPlaying(false);
      }

      onMouseLeave?.();
    }, [displayOptions.animations, onMouseLeave]);

    useEffect(() => {
      if (isActive) {
        if (displayOptions.animations.includes(StoryAnimation.Autoplay)) {
          setIsPlaying(true);
        }
      } else {
        setIsPlaying(false);
      }
    }, [displayOptions.animations, isActive]);

    useEffect(() => {
      if (isPlaying) {
        restartVideo();
      } else {
        stopVideo();
      }
    }, [isPlaying, restartVideo, stopVideo]);

    const withOverlay = useMemo(() => {
      if (displayOptions.display == StoryDisplay.Round) {
        return !isActive;
      }
      if (displayOptions.display == StoryDisplay.Square) {
        return displayOptions.withTitle || withShoppables;
      }
      return false;
    }, [
      displayOptions.display,
      isActive,
      withShoppables,
      displayOptions.withTitle,
    ]);

    if (!story) {
      return null;
    }

    const commonProps = {
      border: displayOptions.border,
      borderColor: displayOptions.borderColor,
      font: displayOptions.font,
      preload,
      story,
      style,
      video,
      videoRef,
      withBorder: displayOptions.border === 'solid',
      withOverlay,
      withPlayIcon: isActive ? false : displayOptions.withPlayIcon,
      withTitle: displayOptions.withTitle,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onTouchStart: handlePreloadDialog,
    };

    if (displayOptions.display === StoryDisplay.Round) {
      return (
        <RoundStory
          {...commonProps}
          isPulsating={isPulsating}
          onClick={() => handleOpenDialog()}
        />
      );
    }

    return (
      <SquareStory
        {...commonProps}
        withRadius={displayOptions.withRadius}
        withShoppables={withShoppables}
        onClick={handleOpenDialog}
      />
    );
  }
);

Story.displayName = 'Story';
